import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';
import { mutatePogiCartLines } from '@pogi/cart-mutation';
import {
  assertPogiVariantPlanPair,
  fetchPogiTextWithTimeout,
  isPogiSameQueuedIntent,
  normalizePogiQueuedQuantity,
  queuePogiGenerationIntent,
  withPogiDeadline,
} from '@pogi/product-queue';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const CART_REQUEST_TIMEOUT_MS = 10000;
const VARIANT_REQUEST_TIMEOUT_MS = 10000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {string | null} syncVariantId - The Pogi-synchronized hidden variant paired with the captured plan.
 * @property {string | null} sellingPlanId - The selling plan selected when Add was clicked.
 * @property {Record<string, string>} properties - Line-item properties captured when Add was clicked.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  /** @type {boolean} */
  #cartAddInFlight = false;

  /**
   * Exact intents currently owned by the native product form. A direct add has
   * one entry; a queued batch may have several. Only a fully identical submit
   * is collapsed while these cart requests are in flight.
   * @type {Array<Pick<QueuedAddToCartItem, 'generation' | 'intendedVariantId' | 'sellingPlanId' | 'quantity' | 'properties'>>}
   */
  #activeCartIntents = [];

  /**
   * Raw click-time intents removed from the queue and currently resolving.
   * They remain owned until the whole drain, including its cart batch, settles.
   * @type {QueuedAddToCartItem[]}
   */
  #drainingCartIntents = [];

  /** @type {boolean} */
  #queueDrainInProgress = false;

  /**
   * A transport-uncertain add may still commit at Shopify after this page loses
   * the response. Once latched, no queued or newly clicked intent may issue
   * another POST before the authoritative cart handoff.
   * @type {boolean}
   */
  #cartStatusUncertain = false;

  /** @type {boolean} */
  #variantChangeFailed = false;

  /** @type {boolean} */
  #variantUnavailable = false;

  /** @type {Array<{container: AddToCartComponent, wasDisabled: boolean}>} */
  #cartAddButtonStates = [];

  /**
   * Pogi buy-box and sticky CTAs are form-associated buttons rendered outside
   * the Horizon `add-to-cart-component`. Keep them under the same in-flight
   * lock as Horizon's native controls so an impatient second tap cannot create
   * a visual operation that Product Form correctly deduplicates.
   * @type {Array<{button: HTMLButtonElement, wasDisabled: boolean, lockObserver: MutationObserver}>}
   */
  #pogiCartAddButtonStates = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.#variantUnavailable = this.#isAddToCartDisabled();

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * A post-commit quantity-label failure must never turn a reconciled cart add
   * into a second, contradictory error settlement.
   * @param {Cart} cart
   */
  #safeUpdateCartQuantity(cart) {
    try {
      this.#updateCartQuantity(cart);
    } catch (error) {
      console.warn('[product-form] Cart quantity UI refresh failed:', error);
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          return this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#cartStatusUncertain) return;

    if (this.#variantChangeFailed) {
      const error = new Error(this.#addErrorMessage());
      error.commitState = 'variant-resolution';
      error.retrySafe = true;
      this.#dispatchMutationError(error);
      return;
    }

    if (this.#cartAddInFlight || this.#variantChangeInProgress || this.#queueDrainInProgress) {
      const queuedItem = this.#createQueuedAddToCartItem();
      if (
        [...this.#activeCartIntents, ...this.#drainingCartIntents].some((ownedIntent) =>
          isPogiSameQueuedIntent(ownedIntent, queuedItem)
        )
      ) {
        return;
      }
      if (queuePogiGenerationIntent(this.#addToCartQueue, queuedItem) === 'duplicate') return;
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;
    const form = this.querySelector('form');
    const sellingPlan = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('[name="selling_plan"]') ?? null
    )?.value;
    const syncVariantId = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input[name="id"][data-pogi-sync="1"]') ?? null
    )?.value;
    const settledVariantId = !this.#variantChangeInProgress ? this.#getVariantIdInput() : null;
    const properties = {};
    if (form instanceof HTMLFormElement) {
      for (const [name, value] of new FormData(form).entries()) {
        const match = name.match(/^properties\[(.*)\]$/);
        if (match && typeof value === 'string' && value !== '') {
          properties[match[1]] = value;
        }
      }
    }

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: syncVariantId || selectedOption?.dataset.variantId || settledVariantId || null,
      syncVariantId: syncVariantId || null,
      sellingPlanId: sellingPlan || null,
      properties,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  #addErrorMessage() {
    return String(Theme.translations.add_to_cart_error || 'Error adding to cart');
  }

  /** @param {string} [message] */
  #showAddError(message = this.#addErrorMessage()) {
    const { addToCartTextError } = this.refs;
    const errorMessage = String(message || this.#addErrorMessage());
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = errorMessage;
      } else {
        addToCartTextError.appendChild(document.createTextNode(errorMessage));
      }
    }
    this.#setLiveRegionText(errorMessage);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {NodeListOf<AddToCartComponent> | AddToCartComponent[]} containers
   * @param {Array<Pick<QueuedAddToCartItem, 'generation' | 'intendedVariantId' | 'sellingPlanId' | 'quantity' | 'properties'>>} [activeIntents]
   */
  #beginCartAdd(containers, activeIntents = []) {
    if (this.#cartAddInFlight) return false;
    this.#cartAddInFlight = true;
    this.#activeCartIntents = activeIntents;
    this.#cartAddButtonStates = Array.from(containers).map((container) => ({
      container,
      wasDisabled: container.refs.addToCartButton?.disabled === true,
    }));
    const form = this.querySelector('form');
    this.#pogiCartAddButtonStates = form instanceof HTMLFormElement
      ? Array.from(form.elements)
          .filter(
            (control) =>
              control instanceof HTMLButtonElement
              && control.matches('[data-pogi-atc], .pogi-satc__btn')
          )
          .map((button) => {
            const lockObserver = new MutationObserver(() => {
              if (button.dataset.pogiAddLock === '1' && !button.disabled) button.disabled = true;
            });
            lockObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
            return { button, wasDisabled: button.disabled, lockObserver };
          })
      : [];
    for (const { container } of this.#cartAddButtonStates) container.disable();
    for (const { button } of this.#pogiCartAddButtonStates) {
      button.dataset.pogiAddLock = '1';
      button.disabled = true;
    }
    return true;
  }

  /** @param {boolean} [restoreButtons] */
  #endCartAdd(restoreButtons = true) {
    this.#cartAddInFlight = false;
    this.#activeCartIntents = [];
    if (restoreButtons && !this.#cartStatusUncertain) {
      for (const { container, wasDisabled } of this.#cartAddButtonStates) {
        if (
          !wasDisabled &&
          !this.#variantChangeInProgress &&
          !this.#variantChangeFailed &&
          !this.#variantUnavailable
        ) {
          container.enable();
        }
      }
      for (const { button, wasDisabled, lockObserver } of this.#pogiCartAddButtonStates) {
        lockObserver.disconnect();
        delete button.dataset.pogiAddLock;
        if (
          !wasDisabled &&
          !this.#variantChangeInProgress &&
          !this.#variantChangeFailed &&
          !this.#variantUnavailable
        ) {
          button.disabled = false;
        }
      }
    }
    this.#cartAddButtonStates = [];
    this.#pogiCartAddButtonStates = [];
    if (
      !this.#cartStatusUncertain &&
      this.#addToCartQueue.length > 0 &&
      !this.#variantChangeInProgress &&
      !this.#queueDrainInProgress
    ) {
      void this.#drainAddToCartQueue();
    }
  }

  /**
   * Keep every form-associated CTA locked until the authoritative drawer
   * section has finished morphing. The cart line may be committed before this
   * view-only barrier settles; unlocking in that gap creates an accidental
   * second-add affordance while the shopper still sees "Adding…".
   * @param {CartLinesUpdateEvent} event
   */
  async #awaitCartPresentation(event) {
    const presentation = event.pogiSectionRenderPromise;
    if (!presentation || typeof presentation.then !== 'function') return true;
    try {
      return (await presentation) !== false;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[product-form] Cart drawer rendering failed:', error);
      }
      return false;
    }
  }

  #failClosedForCartPresentation() {
    this.#cartStatusUncertain = true;
    this.#addToCartQueue = [];
    this.#disableUncertainSubmitControls();
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    this.dataset.cartPresentationFailed = 'true';
    this.dataset.cartStatusUncertain = 'true';
    this.#showAddError(
      Theme.translations.cart_status_uncertain ||
      'Cart status changed. Opening your cart…'
    );
    setTimeout(() => window.location.assign(Theme.routes.cart_url), 2500);
  }

  /** @param {FormData} formData */
  #cartItemFromFormData(formData) {
    const item = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1,
    };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    const properties = {};
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.*)\]$/);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length > 0) item.properties = properties;
    return item;
  }

  /** @param {NodeListOf<AddToCartComponent> | AddToCartComponent[]} containers */
  #announceAddSuccess(containers) {
    const anyAddToCartButton = containers[0]?.refs.addToCartButton;
    if (!anyAddToCartButton) return;
    const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
    const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
    this.#setLiveRegionText(addedText);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  #dispatchMutationError(error) {
    // Shopify response descriptions are intended for shoppers. Internal
    // diagnostics stay in the event detail and never leak raw English into a
    // localized storefront.
    const message =
      error?.response?.description ||
      error?.userMessage ||
      this.#addErrorMessage();
    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: error?.commitState === 'rejected' ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        detail: {
          description: error?.response?.description,
          errors: error?.response?.errors,
          commitState: error?.commitState,
          retrySafe: error?.retrySafe,
          technicalMessage: error?.message,
        },
      })
    );
    this.#showAddError(message);
    return message;
  }

  #disableUncertainSubmitControls() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;
    for (const control of form.elements) {
      const isSubmitButton =
        (control instanceof HTMLButtonElement && control.type === 'submit') ||
        (control instanceof HTMLInputElement && ['submit', 'image'].includes(control.type));
      if (isSubmitButton) control.disabled = true;
    }
  }

  /**
   * @param {*} error
   * @param {*} deferredEventPromise
   * @param {number} itemCount
   */
  #settleMutationError(error, deferredEventPromise, itemCount) {
    this.#dispatchMutationError(error);
    if (error?.currentCart) {
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(error.currentCart),
        detail: {
          didError: true,
          items: error.currentCart.items,
          source: 'product-form-component',
          sourceId: this.id.toString(),
          itemCount,
          productId: this.dataset.productId,
          commitState: error.commitState,
          retrySafe: error.retrySafe,
        },
      });
    } else {
      deferredEventPromise.reject(error);
    }
    if (error?.retrySafe === false) {
      this.#cartStatusUncertain = true;
      this.#addToCartQueue = [];
      this.#disableUncertainSubmitControls();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      this.dataset.cartStatusUncertain = 'true';
      this.#showAddError(
        Theme.translations.cart_status_uncertain ||
        'Cart status changed. Opening your cart…'
      );
      // Keep the error in the live region long enough to be perceived before
      // moving the shopper to the authoritative server-rendered cart.
      setTimeout(() => window.location.assign(Theme.routes.cart_url), 2500);
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;
    const returnFocusTo =
      event instanceof SubmitEvent && event.submitter instanceof HTMLElement
        ? event.submitter
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) {
        if (event) {
          const error = new Error(this.#addErrorMessage());
          error.commitState = 'variant-resolution';
          error.retrySafe = true;
          this.#dispatchMutationError(error);
        }
        return;
      }
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const intendedItem = this.#cartItemFromFormData(formData);
    const activeIntent = {
      generation: this.#variantChangeGeneration,
      intendedVariantId: String(intendedItem.id || ''),
      sellingPlanId: intendedItem.selling_plan ? String(intendedItem.selling_plan) : null,
      quantity: intendedItem.quantity,
      properties: intendedItem.properties || {},
    };
    if (!this.#beginCartAdd(allAddToCartContainers, [activeIntent])) {
      const error = new Error('Cart add already in progress');
      error.commitState = 'client-busy';
      error.retrySafe = true;
      this.#dispatchMutationError(error);
      return;
    }
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    const cartLinesUpdateEvent = new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
        detail: {
          sellingPlanIds: [intendedItem.selling_plan ? String(intendedItem.selling_plan) : null],
          source: 'product-form-component',
          sourceId: this.id.toString(),
        },
      });
    cartLinesUpdateEvent.pogiReturnFocusTo = returnFocusTo;
    this.dispatchEvent(cartLinesUpdateEvent);

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const { body: _body, ...requestOptions } = fetchCfg;
    let restoreButtons = true;
    let mutationSucceeded = false;
    try {
      const result = await mutatePogiCartLines({
        cartAddUrl: Theme.routes.cart_add_url,
        cartJsonUrl: `${Theme.routes.cart_url}.js`,
        items: [intendedItem],
        requestBody: formData,
        requestOptions: {
          ...requestOptions,
          headers: { ...requestOptions.headers, Accept: 'application/json' },
        },
        timeoutMs: CART_REQUEST_TIMEOUT_MS,
      });
      const { cart, addPayload } = result;
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      this.#announceAddSuccess(allAddToCartContainers);
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: this.id.toString(),
          itemCount,
          productId: this.dataset.productId,
          sections: addPayload?.sections,
          didError: false,
          recovered: result.recovered,
        },
      });
      mutationSucceeded = true;
      if (this.#getVariantIdInput()) this.#safeUpdateCartQuantity(cart);
    } catch (error) {
      restoreButtons = error?.retrySafe !== false;
      this.#settleMutationError(error, deferredEventPromise, itemCount);
    } finally {
      if (restoreButtons && mutationSucceeded) {
        const presentationReady = await this.#awaitCartPresentation(cartLinesUpdateEvent);
        if (!presentationReady) {
          restoreButtons = false;
          this.#failClosedForCartPresentation();
        }
      }
      this.#endCartAdd(restoreButtons);
      if (event) cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * @param {Array<{variantId: string, quantity: number, sellingPlanId: string | null, properties: Record<string, string>}>} items
   * @param {QueuedAddToCartItem[]} sourceIntents
   */
  async #processBatchAddToCart(items, sourceIntents) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    if (!this.#beginCartAdd(allAddToCartContainers, sourceIntents)) {
      const error = new Error('Cart add already in progress');
      error.commitState = 'client-busy';
      error.retrySafe = true;
      this.#dispatchMutationError(error);
      return;
    }
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    const cartLinesUpdateEvent = new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
        detail: {
          sellingPlanIds: items.map((item) => item.sellingPlanId),
          source: 'product-form-component',
          sourceId: this.id.toString(),
        },
      });
    this.dispatchEvent(cartLinesUpdateEvent);

    const payload = {
      items: items.map((item) => {
        const line = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };
        if (item.sellingPlanId) line.selling_plan = Number(item.sellingPlanId);
        if (Object.keys(item.properties || {}).length > 0) line.properties = item.properties;
        return line;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    const intendedItems = payload.items;
    let restoreButtons = true;
    let mutationSucceeded = false;
    try {
      const result = await mutatePogiCartLines({
        cartAddUrl: Theme.routes.cart_add_url,
        cartJsonUrl: `${Theme.routes.cart_url}.js`,
        items: intendedItems,
        requestBody: JSON.stringify(payload),
        requestOptions: {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
        timeoutMs: CART_REQUEST_TIMEOUT_MS,
      });
      const { cart, addPayload } = result;
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      this.#announceAddSuccess(allAddToCartContainers);
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: this.id.toString(),
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: addPayload?.sections,
          didError: false,
          recovered: result.recovered,
        },
      });
      mutationSucceeded = true;
      this.#safeUpdateCartQuantity(cart);
    } catch (error) {
      restoreButtons = error?.retrySafe !== false;
      this.#settleMutationError(error, deferredEventPromise, totalQuantity);
    } finally {
      if (restoreButtons && mutationSucceeded) {
        const presentationReady = await this.#awaitCartPresentation(cartLinesUpdateEvent);
        if (!presentationReady) {
          restoreButtons = false;
          this.#failClosedForCartPresentation();
        }
      }
      this.#endCartAdd(restoreButtons);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    this.#variantChangeFailed = false;
    delete this.dataset.variantResolutionFailed;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    try {
      const { detail } = await withPogiDeadline(
        event.promise,
        VARIANT_REQUEST_TIMEOUT_MS,
        'Variant selection',
      );
      if (!detail?.html) {
        throw new Error('The selected variant could not be resolved');
      }
      if (generation !== this.#variantChangeGeneration) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      const variantUnavailable = resource == null || resource.available == false;
      this.#variantUnavailable = variantUnavailable;
      const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );
      for (const container of allAddToCartContainers) {
        if (variantUnavailable || this.#cartAddInFlight || this.#cartStatusUncertain) container.disable();
        else container.enable();
      }

      if (acceleratedCheckoutButtonContainer) {
        if (variantUnavailable || this.#cartAddInFlight || this.#cartStatusUncertain) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      // Keep the surviving Horizon quantity component and its server-rendered
      // cart count explicitly attributed to the variant whose section payload
      // just settled. Buy Box v2 accepts that count only after both move together.
      if (quantitySelector) {
        const settledVariantId = String(resource?.id ?? '');
        const settledCartQuantityValue = newQuantityInput?.getAttribute('data-cart-quantity');
        const settledCartQuantity = Number(settledCartQuantityValue);
        if (
          settledVariantId
          && settledCartQuantityValue !== null
          && settledCartQuantityValue !== undefined
          && Number.isSafeInteger(settledCartQuantity)
          && settledCartQuantity >= 0
        ) {
          if (typeof quantitySelector.setCartQuantity === 'function') {
            quantitySelector.setCartQuantity(settledCartQuantity);
          } else {
            quantitySelector.querySelector('input[name="quantity"]')
              ?.setAttribute('data-cart-quantity', String(settledCartQuantity));
          }
          quantitySelector.dataset.variantId = settledVariantId;
        } else {
          quantitySelector.dataset.variantId = '';
        }
      }

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart()
        .then((cart) => this.#updateCartQuantity(cart))
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[product-form] cart quantity refresh failed:', error);
        });
    } catch (error) {
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeFailed = true;
        this.#variantUnavailable = true;
        this.dataset.variantResolutionFailed = 'true';
        const containers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        for (const container of containers) container.disable();
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        error.commitState = 'variant-resolution';
        error.retrySafe = true;
        this.#dispatchMutationError(error);
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        this.#pendingVariantChange = null;

        // Each queued shopper intent owns its captured generation and resolution
        // URL, so even a later visible-selection failure cannot erase it.
        if (!this.#cartStatusUncertain && this.#addToCartQueue.length > 0) {
          await this.#drainAddToCartQueue();
        }
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#cartStatusUncertain) {
      this.#addToCartQueue = [];
      return;
    }
    if (this.#queueDrainInProgress || this.#cartAddInFlight || this.#addToCartQueue.length === 0) return;

    this.#queueDrainInProgress = true;
    const queuedItems = [...this.#addToCartQueue];
    this.#drainingCartIntents = queuedItems;
    this.#addToCartQueue = [];

    /** @type {Array<{variantId: string, quantity: number, sellingPlanId: string | null, properties: Record<string, string>}>} */
    const resolvedItems = [];
    /** @type {QueuedAddToCartItem[]} */
    const resolvedSourceIntents = [];
    try {
      for (const item of queuedItems) {
        try {
          resolvedItems.push(await this.#resolveQueuedAddToCartItem(item));
          resolvedSourceIntents.push(item);
        } catch (error) {
          error.commitState = 'variant-resolution';
          error.retrySafe = true;
          this.#dispatchMutationError(error);
        }
      }
      if (!this.#cartStatusUncertain && resolvedItems.length > 0) {
        await this.#processBatchAddToCart(resolvedItems, resolvedSourceIntents);
      }
    } finally {
      this.#drainingCartIntents = [];
      this.#queueDrainInProgress = false;
      if (
        !this.#cartStatusUncertain &&
        this.#addToCartQueue.length > 0 &&
        !this.#variantChangeInProgress
      ) {
        await this.#drainAddToCartQueue();
      }
    }
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number, sellingPlanId: string | null, properties: Record<string, string>} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) throw new Error('The selected variant could not be resolved');

    return {
      variantId,
      quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints),
      sellingPlanId: item.sellingPlanId,
      properties: item.properties,
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await withPogiDeadline(
            item.pendingVariantChange,
            VARIANT_REQUEST_TIMEOUT_MS,
            'Queued variant selection',
          )
        );
        if (!result?.detail?.html) throw new Error('Queued variant selection returned no HTML');
        const resource = result?.detail?.resource;
        if (resource !== null && (!resource || typeof resource !== 'object')) {
          throw new Error('Queued variant selection returned invalid data');
        }
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    assertPogiVariantPlanPair(item.syncVariantId, resolvedVariantId);

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    return normalizePogiQueuedQuantity(quantity, {
      min: parseIntOrDefault(quantityConstraints.min, 1),
      max: parseIntOrDefault(quantityConstraints.max, null),
      step: parseIntOrDefault(quantityConstraints.step, 1),
      cartQuantity: parseIntOrDefault(quantityConstraints.cartQuantity, 0),
    });
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const { response, text } = await fetchPogiTextWithTimeout(variantResolutionUrl, {
      timeoutMs: VARIANT_REQUEST_TIMEOUT_MS,
    });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

    const html = new DOMParser().parseFromString(text, 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return { variantId: null, available: false, quantityConstraints };

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
